
import React, { useEffect, useMemo, useState } from 'react';
import Backend from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { FaIcon } from '../../../common/Icons.jsx';
import { useDispatch, useSelector } from 'react-redux';
import Button from '../../../bootstrap/Button.jsx';
import styles from '../../settings_sidebar/ag_chart/series/SeriesSettings.module.scss';
import { DragHandle, DropArea } from '../../../common/DragNDrop.jsx';
import AdvancedFilterButtonSidebar from './AdvancedFilterButtonSidebar.jsx';
import TextInput from '../../../common/TextInput.jsx';
import { SIDEBAR_SETTING_TYPES } from '../../settings_sidebar/DetailSidebarSettings.jsx';
import { openDetail } from '../../redux/dashboardSlice.js';
import { useSetArrayEntityAttribute } from '../../hooks/useSetArrayEntityAttribute.js';
import DatasourceSettings from '../../settings_sidebar/datasource/DatasourceSettings.jsx';
import CollapsibleSection from '../../../common/CollapsibleSection.jsx';
import { useSetWidgetAttribute } from '../../hooks/useSetWidgetAttribute.js';
import { useAdvancedFilterSettings } from '../../hooks/useAdvancedFilterSettings.js';
import SelectSetting from '../../settings_sidebar/common/SelectSetting.jsx';
import SettingsSection from '../../settings_sidebar/common/SettingsSection.jsx';
import SliderSetting from '../../settings_sidebar/common/SliderSetting.jsx';
import TextStyleSettings from '../../settings_sidebar/ag_chart/components/TextStyleSettings.jsx';

const DND_ITEM_TYPE = '#filter-group-button';

function GridButtonNameEdit({ editingId, widgetId, setEditingId, button }) {
  const [value, setValue] = useState(button.name || '')
  const setAttribute = useSetArrayEntityAttribute({widgetId, entitiesSettingId: 'buttons', entityId: button.id });
  const editing = editingId === button.id;

  const toggleEditing = () => {
    if (editingId === button.id) {
      setAttribute('name', value);
      setEditingId(null);
    } else {
      setEditingId(button.id);
    }
  }

  return (
    <div className={styles['title-edit']}>
      <h4 className='text-truncate'>
        {editing
          ? <TextInput value={value} onChange={e => setValue(e.target.value)} style={{height: 20}} />
          : (button.name || `${I18n.t('button')} ${button.id}`)
        }
      </h4>
      <a onClick={toggleEditing}>
        <FaIcon type={editing ? 'check' : 'pencil'} />
      </a>
    </div>
  )
}

function GridWidgetSettings({ widgetId }) {
  const dispatch = useDispatch();
  const [openedButton, setOpenedButton] = useState(null);
  const { addButton, moveButton, removeButton } = useAdvancedFilterSettings(widgetId);
  const setAttribute = useSetWidgetAttribute(widgetId);
  const buttons = useSelector(s => s.dashboard.widgets[widgetId].settings.buttons || []);
  const selectedButton = useMemo(() => openedButton && buttons.find(s => s.id === openedButton), [openedButton, buttons]);
  const [editingId, setEditingId] = useState(null);
  const buttonsErrors = useSelector(state => state.dashboard.configErrors?.[widgetId]?.buttons) || {}
  const selectedObject = useSelector(state => state.dashboard.widgets[widgetId].settings);

  const openModal = (buttonId) => {
    setOpenedButton(buttonId);
    dispatch(openDetail(`${SIDEBAR_SETTING_TYPES.advanced_filter_button}-${buttonId}`));
  }

  const widgetSettingProps = {
    prefix: '',
    selectedObject,
    widgetId,
    setAttribute: (path, val) => setAttribute(`settings.${path}`, val)
  }

  return (
    <>
      <hr />
      <CollapsibleSection title={I18n.t('content_settings')}>
        <TextStyleSettings {...widgetSettingProps} collapseHeadingSize={5} title={I18n.t('label')} prefix='label' />
        <hr />
        <SettingsSection>
          <SelectSetting {...widgetSettingProps} attr='buttonSize' label={I18n.t('button_size')} hideDescription={true}
            options={[
              {value: 'xs', label: I18n.t('extra_small')}, {value: 'sm', label: I18n.t('small')},
              {value: 'md', label: I18n.t('medium')}, {value: 'lg', label: I18n.t('large')}
            ]}
          />
          <SliderSetting {...widgetSettingProps} min={0} max={20} attr='buttonSpacing' label={I18n.t('button_spacing')} hideDescription={true} />
          <SelectSetting {...widgetSettingProps} attr='buttonAlignment' label={I18n.t('button_alignment')} hideDescription={true}
            options={[
              {label: I18n.t('top_left'), value: 'top-left'}, {label: I18n.t('top_center'), value: 'top-center'}, {label: I18n.t('top_right'), value: 'top-right'},
              {label: I18n.t('center_left'), value: 'center-left'}, {label: I18n.t('center'), value: 'center'}, {label: I18n.t('center_right'), value: 'center-right'},
              {label: I18n.t('bottom_left'), value: 'bottom-left'}, {label: I18n.t('bottom_center'), value: 'bottom-center'}, {label: I18n.t('bottom_right'), value: 'bottom-right'}
            ]}
          />
          <DndProvider backend={Backend} context={window}>
            <ul className={`list-group ${styles['list-group']}`}>
              {buttons.map((b, idx) => (
                <DropArea
                  key={idx}
                  accept={DND_ITEM_TYPE}
                  item={{type: DND_ITEM_TYPE, index: idx}}
                  onMove={moveButton}
                  render={({ dropRef, dragRef, isDragging }) => {
                    return (
                      <div 
                        ref={dropRef} 
                        key={idx} 
                        className={`list-group-item dragHandle ${styles['list-group-item']} ${isDragging ? styles['dragging']: ''} ${buttonsErrors[b.id] ? 'form-error' : ''}`}
                      >
                        <DragHandle dragRef={dragRef} sortHandleClass={styles['sort-handle']} />
                        <div className={styles['list-group-item-icon']} style={{margin: 'auto auto'}}>
                          <FaIcon type='filter' style={{color: b.color}} />
                        </div>
                        <div className={styles['list-group-item-content']}>
                          <div className={styles['list-group-item-description']}>
                            <GridButtonNameEdit
                              editingId={editingId}
                              key={b.id}
                              widgetId={widgetId}
                              button={b}
                              setEditingId={setEditingId}
                            />
                            <p className={false ? 'text-danger' : ''}>
                              {b.description || I18n.t('advanced_filter_widget_button')}
                            </p>
                          </div>
                          <div className={styles['list-group-item-actions']}>
                            <a onClick={() => openModal(b.id)}>
                              <FaIcon type='cog' style={{color: 'black'}} />
                            </a>
                            <a onClick={() => removeButton(b.id)}>
                              <FaIcon type='trash' className='text-danger' />
                            </a>
                          </div>
                        </div>
                      </div>
                    )}
                  }
                />
              ))}
            </ul>
          </DndProvider>
        </SettingsSection>
        <div className={styles['action-button-container']}>
          <Button size='sm' onClick={addButton}>
            <FaIcon type='plus' /> {I18n.t('add_button')}
          </Button>
        </div>
      </CollapsibleSection>
      <AdvancedFilterButtonSidebar widgetId={widgetId} selectedButton={selectedButton} setOpenedButton={setOpenedButton} />
    </>
  )
}

export default GridWidgetSettings;
